// src/pages/InscritosCampanha.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import "./InscritosCampanha.css";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import { FileSpreadsheet, Search, Trophy, Users } from "lucide-react";

function InscritosCampanha() {
  const [inscritos, setInscritos] = useState([]);
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroDataInicio, setFiltroDataInicio] = useState("");
  const [filtroDataFim, setFiltroDataFim] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);

  const itensPorPagina = 10;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const campanhaId = queryParams.get("campanha");

  useEffect(() => {
    if (campanhaId) {
      axios
        .get(`/campanhas/${campanhaId}/inscritos`)
        .then((res) => {
          setInscritos(Array.isArray(res.data) ? res.data : []);
        })
        .catch((err) => console.error("Erro ao carregar inscritos:", err));
    }
  }, [campanhaId]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroCliente, filtroDataInicio, filtroDataFim]);

  const inscritosFiltrados = useMemo(() => {
    return inscritos.filter((i) => {
      const clienteMatch = (i.nome || "")
        .toLowerCase()
        .includes(filtroCliente.toLowerCase());

      const data = new Date(i.data_inscricao);
      const inicio = filtroDataInicio ? new Date(filtroDataInicio) : null;
      const fim = filtroDataFim ? new Date(filtroDataFim) : null;

      const dataMatch = (!inicio || data >= inicio) && (!fim || data <= fim);

      return clienteMatch && dataMatch;
    });
  }, [inscritos, filtroCliente, filtroDataInicio, filtroDataFim]);

  const inscritosOrdenados = useMemo(() => {
    return [...inscritosFiltrados].sort(
      (a, b) => (b.pontos_acumulados || 0) - (a.pontos_acumulados || 0)
    );
  }, [inscritosFiltrados]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(inscritosOrdenados.length / itensPorPagina)
  );

  const inscritosPagina = inscritosOrdenados.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const totalPontos = inscritosFiltrados.reduce(
    (soma, i) => soma + (i.pontos_acumulados || 0),
    0
  );

  const maiorPontuacao = inscritosFiltrados.reduce(
    (maior, i) => Math.max(maior, i.pontos_acumulados || 0),
    0
  );

  const exportarExcel = () => {
    import("exceljs").then((ExcelJS) => {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Inscritos");

      ws.addRow(["Nome", "Email", "Data de Inscrição", "Pontos"]);

      inscritosOrdenados.forEach((i) => {
        ws.addRow([
          i.nome,
          i.email,
          format(new Date(i.data_inscricao), "dd/MM/yyyy HH:mm"),
          i.pontos_acumulados ?? 0,
        ]);
      });

      wb.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = "inscritos_campanha.xlsx";
        a.click();

        window.URL.revokeObjectURL(url);
      });
    });
  };

  const limparFiltros = () => {
    setFiltroCliente("");
    setFiltroDataInicio("");
    setFiltroDataFim("");
  };

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  const formatarData = (data) => {
    if (!data) return "—";

    try {
      return format(new Date(data), "dd/MM/yyyy HH:mm");
    } catch {
      return "—";
    }
  };

  return (
    <div className="inscritosCampanhaAdmin-page">
      <div className="inscritosCampanhaAdmin-topbar">
        <div>
          <span className="inscritosCampanhaAdmin-kicker">
            Campanhas promocionais
          </span>

          <h2>Inscrições na Campanha</h2>

          <p>
            Acompanhe os clientes inscritos, filtre por nome ou período e
            exporte a relação para Excel.
          </p>
        </div>

        <button
          type="button"
          className="inscritosCampanhaAdmin-btn primary"
          onClick={exportarExcel}
        >
          <FileSpreadsheet size={16} />
          Exportar Excel
        </button>
      </div>

      <div className="inscritosCampanhaAdmin-cards">
        <div className="inscritosCampanhaAdmin-card">
          <span>Inscritos filtrados</span>
          <strong>{formatarNumero(inscritosFiltrados.length)}</strong>
        </div>

        <div className="inscritosCampanhaAdmin-card success">
          <span>Total de pontos</span>
          <strong>{formatarNumero(totalPontos)}</strong>
        </div>

        <div className="inscritosCampanhaAdmin-card warning">
          <span>Maior pontuação</span>
          <strong>{formatarNumero(maiorPontuacao)}</strong>
        </div>
      </div>

      <section className="inscritosCampanhaAdmin-filtrosCard">
        <div className="inscritosCampanhaAdmin-filtrosHeader">
          <div>
            <h3>Filtros</h3>
            <span>Refine a listagem por cliente ou data de inscrição.</span>
          </div>

          <Users size={18} />
        </div>

        <div className="inscritosCampanhaAdmin-filtros">
          <div className="inscritosCampanhaAdmin-filtro busca">
            <label>Cliente</label>
            <div className="inscritosCampanhaAdmin-searchInput">
              <Search size={15} />
              <input
                type="text"
                placeholder="Filtrar por cliente"
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
              />
            </div>
          </div>

          <div className="inscritosCampanhaAdmin-filtro">
            <label>Data início</label>
            <input
              type="date"
              value={filtroDataInicio}
              onChange={(e) => setFiltroDataInicio(e.target.value)}
            />
          </div>

          <div className="inscritosCampanhaAdmin-filtro">
            <label>Data fim</label>
            <input
              type="date"
              value={filtroDataFim}
              onChange={(e) => setFiltroDataFim(e.target.value)}
            />
          </div>
        </div>

        <div className="inscritosCampanhaAdmin-filtrosActions">
          <button
            type="button"
            className="inscritosCampanhaAdmin-btn secondary"
            onClick={limparFiltros}
          >
            Limpar filtros
          </button>

          <span>{formatarNumero(inscritosFiltrados.length)} resultado(s)</span>
        </div>
      </section>

      <section className="inscritosCampanhaAdmin-tableCard">
        <div className="inscritosCampanhaAdmin-tableHeader">
          <div>
            <h3>Lista de inscritos</h3>
            <span>Ordenada automaticamente por maior pontuação acumulada.</span>
          </div>

          <Trophy size={18} />
        </div>

        <div className="inscritosCampanhaAdmin-tableWrapper">
          <table className="inscritosCampanhaAdmin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Email</th>
                <th>Data de inscrição</th>
                <th>Pontos</th>
              </tr>
            </thead>

            <tbody>
              {inscritosPagina.length === 0 ? (
                <tr>
                  <td colSpan="4" className="inscritosCampanhaAdmin-empty">
                    Nenhuma inscrição encontrada.
                  </td>
                </tr>
              ) : (
                inscritosPagina.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <div className="inscritosCampanhaAdmin-cellMain">
                        <strong>{i.nome || "—"}</strong>
                        <span>ID #{i.id}</span>
                      </div>
                    </td>

                    <td>
                      <div className="inscritosCampanhaAdmin-cellMain">
                        <span>{i.email || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <div className="inscritosCampanhaAdmin-cellMain">
                        <span>{formatarData(i.data_inscricao)}</span>
                      </div>
                    </td>

                    <td>
                      <span className="inscritosCampanhaAdmin-pontos">
                        {formatarNumero(i.pontos_acumulados ?? 0)} pts
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div className="inscritosCampanhaAdmin-paginacao">
            <button
              type="button"
              onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
              disabled={paginaAtual === 1}
            >
              Anterior
            </button>

            <span>
              Página {paginaAtual} de {totalPaginas}
            </span>

            <button
              type="button"
              onClick={() =>
                setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))
              }
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export default InscritosCampanha;